import { useState, useEffect, useCallback } from "react";
import { api, formatApiError, formatPrice } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  code: "",
  type: "percent",
  amount: 10,
  usage_limit: "",
  expiry_date: "",
  min_order_amount: 0,
  is_active: true,
};

export default function DiscountManager() {
  const [codes, setCodes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/discounts").then(({ data }) => setCodes(data.discounts)).finally(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const resetForm = () => { setForm(EMPTY); setEditingId(null); };

  const startEdit = (d) => {
    setEditingId(d.id);
    setForm({
      code: d.code,
      type: d.type,
      amount: d.amount,
      usage_limit: d.usage_limit ?? "",
      expiry_date: d.expiry_date ? d.expiry_date.slice(0, 10) : "",
      min_order_amount: d.min_order_amount ?? 0,
      is_active: d.is_active,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      code: form.code,
      type: form.type,
      amount: parseFloat(form.amount) || 0,
      usage_limit: form.usage_limit === "" ? null : parseInt(form.usage_limit, 10),
      expiry_date: form.expiry_date || null,
      min_order_amount: parseFloat(form.min_order_amount) || 0,
      is_active: !!form.is_active,
    };
    try {
      if (editingId) {
        await api.put(`/admin/discounts/${editingId}`, payload);
        toast.success("Discount updated");
      } else {
        await api.post("/admin/discounts", payload);
        toast.success("Discount created");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this discount code?")) return;
    try {
      await api.delete(`/admin/discounts/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleActive = async (d) => {
    try {
      await api.put(`/admin/discounts/${d.id}`, {
        code: d.code, type: d.type, amount: d.amount,
        usage_limit: d.usage_limit ?? null, expiry_date: d.expiry_date || null,
        min_order_amount: d.min_order_amount ?? 0, is_active: !d.is_active,
      });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const inputCls = "w-full bg-transparent border-2 border-[#F2EFEB] h-11 px-3 focus:outline-none focus:border-[#DFFF00] text-sm";

  return (
    <div data-testid="discounts-panel" className="grid grid-cols-1 lg:grid-cols-2 gap-10">
      {/* Form */}
      <form onSubmit={save} data-testid="discount-form" className="border-2 border-[#F2EFEB] p-6 h-fit">
        <p className="overline text-[#DFFF00] mb-4">{editingId ? "Edit Code" : "New Discount Code"}</p>

        <label className="overline block mb-1">Code</label>
        <input className={`${inputCls} uppercase`} value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} data-testid="discount-code-input" placeholder="GRIEF10" required />

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="overline block mb-1">Type</label>
            <select className={inputCls} value={form.type} onChange={(e) => set("type", e.target.value)} data-testid="discount-type-select">
              <option value="percent" className="bg-[#090909]">Percentage %</option>
              <option value="flat" className="bg-[#090909]">Flat $</option>
            </select>
          </div>
          <div>
            <label className="overline block mb-1">{form.type === "percent" ? "Percent off" : "Amount off ($)"}</label>
            <input type="number" step="0.01" min="0" className={inputCls} value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="discount-amount-input" required />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4">
          <div>
            <label className="overline block mb-1">Usage limit</label>
            <input type="number" min="0" className={inputCls} value={form.usage_limit} onChange={(e) => set("usage_limit", e.target.value)} data-testid="discount-limit-input" placeholder="unlimited" />
          </div>
          <div>
            <label className="overline block mb-1">Min order ($)</label>
            <input type="number" step="0.01" min="0" className={inputCls} value={form.min_order_amount} onChange={(e) => set("min_order_amount", e.target.value)} data-testid="discount-minorder-input" />
          </div>
        </div>

        <label className="overline block mb-1 mt-4">Expiry date</label>
        <input type="date" className={inputCls} value={form.expiry_date} onChange={(e) => set("expiry_date", e.target.value)} data-testid="discount-expiry-input" />
        <p className="font-mono-ui text-[0.65rem] opacity-60 mt-1">Leave blank for no expiry. Code works through the end of this day.</p>

        <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer">
          <input type="checkbox" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} data-testid="discount-active-input" />
          Active (customers can use it)
        </label>

        <div className="flex gap-3 mt-6">
          <button type="submit" disabled={saving} data-testid="discount-save-btn" className="flex-1 h-12 border-2 border-[#F2EFEB] bg-[#F2EFEB] text-[#090909] uppercase tracking-widest text-sm hover:bg-[#DFFF00] hover:border-[#DFFF00] transition-colors disabled:opacity-60">
            {saving ? "..." : editingId ? "Update →" : "Create →"}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="h-12 px-5 border-2 border-[#FF2A00] text-[#FF2A00] uppercase text-xs hover:bg-[#FF2A00] hover:text-[#090909] transition-colors">Cancel</button>
          )}
        </div>
      </form>

      {/* List */}
      <div data-testid="discount-list">
        <p className="overline text-[#DFFF00] mb-4">{loaded ? `${codes.length} codes` : "loading..."}</p>
        {!loaded ? (
          <p className="opacity-60 py-8 animate-pulse">Loading codes...</p>
        ) : codes.length === 0 ? (
          <p className="opacity-60 py-8 font-mono-ui text-sm">No discount codes yet. Create one to run a promo.</p>
        ) : (
          <div className="space-y-2">
            {codes.map((d) => (
              <div key={d.id} data-testid={`discount-item-${d.code}`} className={`border-2 p-3 ${d.is_active ? "border-[#F2EFEB]/40" : "border-[#FF2A00]/40 opacity-70"}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-display text-2xl uppercase">{d.code}</span>
                  <span className="font-mono-ui text-sm text-[#DFFF00]">
                    {d.type === "percent" ? `${d.amount}% off` : `$${formatPrice(d.amount)} off`}
                  </span>
                  {!d.is_active && <span className="font-mono-ui text-[0.6rem] uppercase bg-[#FF2A00] text-[#090909] px-2 py-0.5">Inactive</span>}
                  <div className="flex-1" />
                  <button onClick={() => toggleActive(d)} data-testid={`discount-toggle-${d.code}`} className="text-xs border-2 border-[#F2EFEB] px-3 py-1 hover:bg-[#DFFF00] hover:text-[#090909] transition-colors">
                    {d.is_active ? "Disable" : "Enable"}
                  </button>
                  <button onClick={() => startEdit(d)} data-testid={`discount-edit-${d.code}`} className="text-xs border-2 border-[#F2EFEB] px-3 py-1 hover:bg-[#DFFF00] hover:text-[#090909] transition-colors">Edit</button>
                  <button onClick={() => del(d.id)} data-testid={`discount-delete-${d.code}`} className="text-xs border-2 border-[#FF2A00] text-[#FF2A00] px-3 py-1 hover:bg-[#FF2A00] hover:text-[#090909] transition-colors">Del</button>
                </div>
                <p className="font-mono-ui text-xs opacity-60 mt-2">
                  Used {d.used_count}{d.usage_limit != null ? ` / ${d.usage_limit}` : " (unlimited)"}
                  {d.min_order_amount > 0 ? ` · min $${formatPrice(d.min_order_amount)}` : ""}
                  {d.expiry_date ? ` · expires ${d.expiry_date.slice(0, 10)}` : " · no expiry"}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
